import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompanyEnrichmentResponse, CompanyResponse, CompanySummaryResponse, ContactResponse, CreateCompanyInput, ListCompaniesQuery, UpdateCompanyInput } from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListCompaniesQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useCompaniesQuery(query: Partial<ListCompaniesQuery>) {
  return useQuery({
    queryKey: ['companies', query],
    queryFn: () => getPage<CompanyResponse[]>(`/companies${toQueryString(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useCompanyQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['companies', id],
    queryFn: () => api.get<CompanyResponse>(`/companies/${id}`),
    enabled: Boolean(id),
  });
}

export function useCompanySummaryQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['companies', id, 'summary'],
    queryFn: () => api.get<CompanySummaryResponse>(`/companies/${id}/summary`),
    enabled: Boolean(id),
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyInput) => api.post<CompanyResponse>('/companies', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useUpdateCompany(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCompanyInput) => api.patch<CompanyResponse>(`/companies/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useCompanyEnrichmentQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['companies', id, 'enrichment'],
    queryFn: () => api.get<CompanyEnrichmentResponse | null>(`/companies/${id}/enrichment`),
    enabled: Boolean(id),
  });
}

export function useEnrichCompany(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CompanyEnrichmentResponse>(`/companies/${id}/enrichment`),
    onSuccess: (data) => queryClient.setQueryData(['companies', id, 'enrichment'], data),
  });
}

export function useAddEnrichedPersonAsContact(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) => api.post<ContactResponse>(`/companies/${companyId}/enrichment/people/${personId}/add-contact`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'enrichment'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}
